import { makeAutoObservable } from "mobx";
import {
  DbotEventDispatcher,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  ProjectDocumentStatus,
} from "../common/commonDomain";
import { Language } from "../../i18n/languages";
import { DocumentLanguage } from "../../common/common";
import { DocumentType } from "../../common/documents";
import {
  downloadSignedSubscriptionFormsAction,
  openSendForSigningDialogAction,
  openSubscriptionFormBatchPreviewDialogAction,
} from "../summary/summaryDomain";
import { DocumentSigningStatus } from "../../document/documentApi";
import { DecisionType } from "../../common/decisions";
import { TMessage } from "../../i18n/tt";
import { FileToPreview } from "../../components-v2/BatchPreviewDialog/batchPreviewDomain";
import {
  documentLanguageLabels,
  RadioFieldModel,
  requiredOfficialDocumentLanguageFieldModel,
} from "../../common/commonForms";

export type SubscriptionForm = {
  type: DocumentType.subscription_form;
  documentLanguage: Language[];
};

export class SubscriptionFormForm {
  readonly type = DocumentType.subscription_form;
  readonly dispatch: DbotEventDispatcher;
  context: DocumentContext<SubscriptionForm>;
  documentLanguage: RadioFieldModel<DocumentLanguage>;

  constructor(dispatch: DbotEventDispatcher, context: DocumentContext<SubscriptionForm>) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.documentLanguage = requiredOfficialDocumentLanguageFieldModel({
      value: mapLocalesToDocumentLanguage(context.documentData.documentLanguage),
    });
  }

  get isLocked(): boolean {
    return this.context.isLocked;
  }

  get documentId() {
    return this.context.documentId;
  }

  get increaseShareCapitalDecision() {
    return this.context.currentDecisions.find(
      (d) => d.type === DecisionType.increaseOfShareCapital,
    );
  }

  get isIncreaseShareCapitalDecisionAdded() {
    return this.increaseShareCapitalDecision !== undefined;
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.documentId);
  }

  get requiredDocumentType() {
    return this.increaseShareCapitalDecision?.includeInGeneralMeeting
      ? DocumentType.general_meeting
      : DocumentType.board_meeting;
  }

  get requiredDocumentLabel(): TMessage {
    return this.increaseShareCapitalDecision?.includeInGeneralMeeting
      ? { key: "documents.subscriptionForm.sendRequiredDocument.generalMeeting" }
      : { key: "documents.subscriptionForm.sendRequiredDocument.boardMeeting" };
  }

  get requiredDocumentItemSentForSigning() {
    return this.context.currentDocuments.find((d) => d.type === this.requiredDocumentType);
  }

  get isRequiredDocumentSentForSigning() {
    return (
      this.context.documentStatuses.find(
        (d) => d.documentId === this.requiredDocumentItemSentForSigning?.documentId,
      )?.sentAt !== undefined
    );
  }

  get isDownloadDocumentDisabled() {
    return this.documentStatus?.numberOfSignedDocuments === 0;
  }

  get showSendRequiredDocument() {
    return this.isIncreaseShareCapitalDecisionAdded && !this.isRequiredDocumentSentForSigning;
  }

  get isSentForSigning() {
    return (
      this.documentStatus !== undefined &&
      [
        DocumentSigningStatus.SENT_FOR_SIGNING,
        DocumentSigningStatus.SIGNING_IN_PROGRESS,
        DocumentSigningStatus.SIGNING_COMPLETE,
      ].includes(this.documentStatus?.signingStatus)
    );
  }

  get downloadSignedSubscriptionFormsAction() {
    return downloadSignedSubscriptionFormsAction({
      document: this.documentItem,
      isSentForSigning: this.isSentForSigning,
      numberOfSignedDocuments: this.documentStatus?.numberOfSignedDocuments,
    });
  }

  openBatchPreviewAction(documentTitle: string) {
    return openSubscriptionFormBatchPreviewDialogAction({
      document: this.documentItem,
      filePreview: (file: FileToPreview) => {
        const filename = `${documentTitle} - ${file.name}.pdf`;
        return {
          filename,
          file: {
            url: `/api/document/view-subscription-form/${this.documentId}?ref=${file.reference}`,
          },
        };
      },
      successAction: { type: "dbot/fetch-increase-of-share-capital-subscribers-status" },
      isSentForSigning: this.isSentForSigning,
    });
  }

  get openSendForSigningDialogAction() {
    if (this.isIncreaseShareCapitalDecisionAdded) {
      return openSendForSigningDialogAction({
        document: this.documentItem,
        signingStatus: this.documentStatus?.signingStatus,
        showUnsupportedAttachmentWarning: false,
      });
    }
  }
}

export function applyForm(form: SubscriptionFormForm): SubscriptionForm {
  return {
    type: DocumentType.subscription_form,
    documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
  };
}

export enum SubscriptionFormCustomPanel {
  settings = "settings",
}

export function getSubscriptionFormCustomPanels(form: SubscriptionFormForm): DetailsPanel[] {
  return [
    {
      type: "custom",
      id: SubscriptionFormCustomPanel.settings,
      documentId: form.documentId,
      icon: "gear",
      heading: { key: "dbot.settings" },
      label: documentLanguageLabels[form.documentLanguage.value],
    },
  ];
}
